/**
 * QR Customize Module Types
 *
 * This module defines the core types for QR code customization functionality.
 * All types use readonly properties for immutable state management.
 *
 * Requirements: 9.3, 9.4
 */

/**
 * Font families available for bay labels
 * - Topgolf: Used for main titles and large headers only
 * - ShieldSans: Default font for everything else (paragraphs, labels, buttons)
 */
export type FontFamily = 'Topgolf' | 'ShieldSans';

/**
 * Preview target type for determining which QR code to preview/generate
 * Discriminated union type for type-safe handling of different target types
 */
export type PreviewTarget =
  | { readonly type: 'site' }
  | { readonly type: 'fixed-bay'; readonly bayId: string; readonly bayNumber: string }
  | { readonly type: 'super-bay'; readonly bayId: string; readonly bayName: string };

/**
 * QR Template - Complete customization configuration
 * Requirement 9.3: Template state includes color, center enabled, font, size, logo
 */
export interface QrTemplate {
  /** QR code foreground color (hex format) */
  readonly color: string;

  /** Whether center customization is enabled */
  readonly centerEnabled: boolean;

  /** Whether to show bay label on fixed bay QR codes (when center is enabled) */
  readonly showBayLabel: boolean;

  /** Font family for bay labels */
  readonly fontFamily: FontFamily;

  /** Font size for bay labels (relative to 256px reference canvas, scales proportionally) */
  readonly fontSize: number;

  /** Font color for bay labels (hex format) */
  readonly bayLabelColor: string;

  /** Logo image as data URL, null if no logo */
  readonly logoDataUrl: string | null;

  /** Padding inside the center overlay as a ratio (0-1), applied to logo/label area */
  readonly centerPadding: number;

  /** Whether to show bay/site ID label in bottom-right corner (enabled by default) */
  readonly showCornerLabel: boolean;

  /** Whether to show the tgo-logo.svg below the QR code (disabled by default) */
  readonly bottomLogo: boolean;
}

/**
 * Default template values
 * Requirements 3.4, 5.6:
 * - Default QR code color to black (#000000)
 * - Default bay label font to "ShieldSans" and font size to a readable default value
 */
export const DEFAULT_TEMPLATE: QrTemplate = {
  color: '#000000',
  centerEnabled: true,
  showBayLabel: true,
  fontFamily: 'ShieldSans',
  fontSize: 40,
  bayLabelColor: '#1a9ed5',
  logoDataUrl: null,
  centerPadding: 0.15,
  showCornerLabel: true,
  bottomLogo: false,
};
